import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { verifyPayment } from "../../services/api";

type PaymentStatus = "verifying" | "success" | "error";

const PaymentSuccessPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<PaymentStatus>("verifying");

  const queryValues = useMemo(() => {
    return {
      sessionId: searchParams.get("session_id") || "",
      dealId: searchParams.get("dealId") || "",
    };
  }, [searchParams]);

  useEffect(() => {
    let cancelled = false;

    const runVerification = async () => {
      if (!queryValues.sessionId) {
        if (queryValues.dealId) {
          navigate(`/deal/view/${queryValues.dealId}?paid=true`, { replace: true });
          return;
        }
        setStatus("error");
        return;
      }

      try {
        await verifyPayment(queryValues.sessionId);

        if (cancelled) return;

        if (window.opener && !window.opener.closed) {
          window.opener.postMessage(
            {
              type: "PAYMENT_SUCCESS",
              sessionId: queryValues.sessionId,
            },
            window.location.origin,
          );
        }

        setStatus("success");

        window.setTimeout(() => {
          try {
            window.close();
          } catch {
          }

          if (queryValues.dealId) {
            window.location.replace(`/deal/view/${queryValues.dealId}?paid=true`);
          }
        }, 1200);
      } catch {
        if (!cancelled) setStatus("error");
      }
    };

    void runVerification();

    return () => {
      cancelled = true;
    };
  }, [navigate, queryValues.dealId, queryValues.sessionId]);

  return (
    <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-green-50 via-white to-emerald-50">
      <div className="text-center px-6 py-12">
        {status === "verifying" ? (
          <div className="animate-fade-in">
            <div className="relative inline-flex items-center justify-center w-32 h-32 mb-8">
              <div className="absolute w-full h-full border-4 border-green-200 rounded-full animate-ping"></div>
              <div className="absolute w-full h-full border-4 border-green-400 rounded-full animate-pulse"></div>
              <div className="relative w-24 h-24 border-4 border-t-green-600 border-r-green-500 border-b-green-400 border-l-green-300 rounded-full animate-spin"></div>
            </div>
            <h2 className="text-2xl font-semibold text-gray-800 mb-2">
              Verifying Payment...
            </h2>
            <p className="text-gray-600">Please wait while we confirm your transaction</p>
          </div>
        ) : status === "success" ? (
          <div className="animate-fade-in">
            <div className="relative inline-flex items-center justify-center mb-8">
              <div className="absolute w-32 h-32 bg-green-100 rounded-full animate-ping opacity-75"></div>
              <div className="relative">
                <svg
                  className="w-32 h-32 text-green-600 animate-bounce-in"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  strokeWidth="1.5"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
            </div>

            <h1 className="text-4xl font-bold text-green-600 mb-4 animate-slide-up">
              Payment Successful!
            </h1>

            <div className="max-w-md mx-auto bg-white rounded-2xl shadow-lg p-8 animate-slide-up-delay">
              <p className="text-lg text-gray-700 mb-6">
                Your payment has been processed successfully.
              </p>

              <p className="text-gray-600">
                You can now close this window.
              </p>
            </div>
          </div>
        ) : (
          <div className="animate-fade-in">
            <div className="relative inline-flex items-center justify-center mb-8">
              <div className="absolute w-32 h-32 bg-red-100 rounded-full"></div>
              <div className="relative">
                <svg
                  className="w-32 h-32 text-red-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  strokeWidth="1.5"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 9v4m0 4h.01M10.29 3.86l-7.4 12.88A2 2 0 004.62 20h14.76a2 2 0 001.73-3.26l-7.4-12.88a2 2 0 00-3.46 0z"
                  />
                </svg>
              </div>
            </div>

            <h1 className="text-4xl font-bold text-red-600 mb-4">
              Payment Failed
            </h1>

            <div className="max-w-md mx-auto bg-white rounded-2xl shadow-lg p-8">
              <p className="text-lg text-gray-700 mb-6">
                We could not verify your payment.
              </p>

              <p className="text-gray-600">
                Please return to the deal page and try again.
              </p>
            </div>
          </div>
        )}
      </div>

      <style>{`
        @keyframes fade-in {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes slide-up {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes bounce-in {
          0% {
            transform: scale(0);
            opacity: 0;
          }
          50% {
            transform: scale(1.1);
          }
          100% {
            transform: scale(1);
            opacity: 1;
          }
        }

        .animate-fade-in {
          animation: fade-in 0.5s ease-out;
        }

        .animate-slide-up {
          animation: slide-up 0.6s ease-out;
        }

        .animate-slide-up-delay {
          animation: slide-up 0.6s ease-out 0.2s both;
        }

        .animate-bounce-in {
          animation: bounce-in 0.6s cubic-bezier(0.68, -0.55, 0.265, 1.55);
        }
      `}</style>
    </div>
  );
};

export default PaymentSuccessPage;